import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { CustomerFormField } from './CustomerFormField';
import { CustomerInput } from './CustomerInput';

export interface CustomerPasswordRule {
  label: string;
  test: (value: string) => boolean;
}

interface CustomerPasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  disabled?: boolean;
  required?: boolean;
  error?: string | null;
  autoFocus?: boolean;
  /** When given, a live rule list is rendered under the field (activation-page pattern). */
  rules?: CustomerPasswordRule[];
  /** Controlled visibility, so a confirm field can share the toggle of the field above it. */
  show?: boolean;
  onToggleShow?: () => void;
}

/**
 * User Profile P2 — password input with a 44px show/hide toggle and an
 * optional live rule list whose "met / not yet met" state is spoken, not only
 * coloured (the activation page's pattern, lifted verbatim).
 */
export function CustomerPasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  disabled = false,
  required = true,
  error,
  autoFocus = false,
  rules,
  show,
  onToggleShow,
}: CustomerPasswordFieldProps) {
  const [localShow, setLocalShow] = useState(false);
  const visible = show ?? localShow;
  const toggle = onToggleShow ?? (() => setLocalShow((v) => !v));
  const rulesId = rules ? `${id}-rules` : undefined;
  const describedBy = [rulesId, error ? `${id}-error` : undefined].filter(Boolean).join(' ');

  return (
    <CustomerFormField label={label} htmlFor={id} required={required} error={error}>
      <div className="relative">
        <CustomerInput
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={disabled}
          invalid={!!error}
          aria-describedby={describedBy || undefined}
          className="pr-11"
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-customer-text-secondary hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-inset"
          aria-label={visible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} className="h-4 w-4" />
        </button>
      </div>
      {rules && (
        <ul id={rulesId} className="mt-2 space-y-1">
          {rules.map((rule) => {
            const met = rule.test(value);
            return (
              <li
                key={rule.label}
                className={`flex items-center gap-1.5 text-xs ${
                  met ? 'text-customer-primary-text' : 'text-customer-text-secondary'
                }`}
              >
                <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" aria-hidden="true" />
                <span>{rule.label}</span>
                <span className="sr-only">{met ? '— met' : '— not yet met'}</span>
              </li>
            );
          })}
        </ul>
      )}
    </CustomerFormField>
  );
}
